import React, { useState } from 'react';
import { RootLink } from './components/RootLink';
import { IdentityWidget } from '../IdentityWidget';
import { useDesktopView } from './useDesktopView';
import { useHeaderLinks } from '../../useHeaderLinks';
import { DesktopMenu } from './components/DesktopMenu';
import { DESKTOP_NAV_HEIGHT } from '../../AppNavBar.constants';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex } from '@withjoy/joykit';
import { animationTransitionExt } from '@shared/utils/animationTransition';
import { HeaderLinksGroupButton } from './components/HeaderLinksGroupButton';
import type { HeaderLinksType, MarcomAppNavBarDesktopProps } from '../../AppNavBar.types';
import { ReactComponent as IconBrandLogoDesktop } from '@assets/icons/icon-brand-logo-desktop.svg';
import { RegistryGiftBasketWidget, WidgetTrigger } from '@apps/registry/common/components/RegistryGiftBasketWidget';
import { DropdownLinksContainer, MEGANAV_STYLE_OVERRIDES, NavContainer, Root } from './DesktopView.styles';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import { pxToRem } from '@withjoy/joykit/theme';
import { CatalogSearchBarInput } from '@apps/registry/common/components/Catalog';
import { useTranslation } from '@shared/core';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { MobileView } from '../MobileView/MobileView';
import { Page } from '@apps/registry/common/components/RegistryGiftCounts/telemetry';
import { useRegistryGiftCounts } from '@apps/registry/common/components/RegistryGiftCounts/RegistryGiftCounts.controller';
import { useEventMemberships } from '@shared/components/AuthProvider';

const Group: React.FC<Omit<BoxProps, '__css'>> = props => {
  return (
    <Box
      display="flex"
      alignItems="center"
      {...props}
    />
  );
};

export const DesktopView = (props: MarcomAppNavBarDesktopProps) => {
  const { currentUser, eventId, eventHandle, onSwitchEventClick } = props;
  const { eventMemberships } = useEventMemberships();
  const {
    getLinksContainerProps,
    getHeaderLinkProps,
    getHeaderLinkGroupButtonProps,
    getRootProps,
    getTranslations,

    linkGroupHeadersRef,
    linksDropdownRef,
    containerRef,
    headerLinksType,
    isOpen,
    topLevelLinks,
  } = useDesktopView();
  const { config } = useShopStorefrontContext();
  const { linkGroupHeaders } = getTranslations();
  const { headerLinks } = useHeaderLinks();
  const headerLinksMenu: HeaderLinksType[] = ['planInvite', 'registry', 'expertAdvice'];
  const { t } = useTranslation('sharedRegistry');
  const isMobile = useIsMobileScreen();
  const catalogSearchBarTrans = t('catalogSearchInput');
  const [searchFocused, setSearchFocused] = useState(false);
  const { completionPercent } = useRegistryGiftCounts({ eventId: props.eventId || '', page: Page.overview, eventHandle: props.eventHandle || '' });

  return (
    <Root
      {...getRootProps()}
      zIndex={25}
      position="sticky"
      top="0"
      height={[pxToRem(66), null, pxToRem(DESKTOP_NAV_HEIGHT)]}
    >
      <NavContainer
        $isOpen={isOpen}
        transition={animationTransitionExt({
          property: 'background-color',
          delay: isOpen ? undefined : '250ms',
          duration: '280ms',
        })}
        height={[pxToRem(66), null, pxToRem(DESKTOP_NAV_HEIGHT)]}
      >
        {!isMobile && (
          <Flex
            data-container
            ref={containerRef}
            {...MEGANAV_STYLE_OVERRIDES}
          >
            <Flex
              flex={1}
              alignItems="center"
            >
              <Box
                as="a"
                href={config.homePageUrl}
              >
                <IconBrandLogoDesktop />
              </Box>
              <Box marginLeft={'48px'}>
                <Box
                  position="relative"
                  ref={linkGroupHeadersRef}
                  zIndex={1}
                  display="flex"
                  columnGap={2}
                >
                  {headerLinksMenu.map((type, idx) =>
                    headerLinks[type].length > 0 ? (
                      <HeaderLinksGroupButton
                        key={`header-menu-${type}-${idx}`}
                        {...getHeaderLinkGroupButtonProps(type)}
                      >
                        {linkGroupHeaders[type]}
                      </HeaderLinksGroupButton>
                    ) : null,
                  )}
                </Box>
              </Box>
            </Flex>

            <Group
              flex={1}
              justifyContent="flex-end"
            >
              {currentUser.profile ? (
                <IdentityWidget
                  tabIndex={0}
                  anchorProps={{ marginLeft: 1 }}
                  profile={currentUser.profile}
                  eventMemberships={eventMemberships}
                  eventId={eventId}
                  onSwitchEventClick={onSwitchEventClick}
                />
              ) : (
                <>
                  <RootLink
                    paddingY={4}
                    paddingX={5}
                    href={topLevelLinks.login.href}
                    onClick={topLevelLinks.login.onClick}
                    marginLeft="36px"
                  >
                    {topLevelLinks.login.label}
                  </RootLink>
                  <ButtonV2
                    as="a"
                    width={144}
                    whiteSpace="nowrap"
                    padding={4}
                    href={topLevelLinks.getStarted.href}
                    onClick={topLevelLinks.getStarted.onClick}
                    marginLeft={5}
                    fontFamily="Akkurat LL"
                    fontWeight={700}
                    fontSize={17}
                  >
                    {topLevelLinks.getStarted.label}
                  </ButtonV2>
                </>
              )}
            </Group>
          </Flex>
        )}
        <Flex
          width="100%"
          alignItems={['flex-end', null, 'flex-start']}
          justifyContent="space-between"
          columnGap={[3, null, 6]}
          paddingX={[5, null, 8]}
          height={[pxToRem(44), null, pxToRem(68)]}
        >
          {!isMobile || searchFocused ? null : <MobileView {...props} />}
          <CatalogSearchBarInput
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder={catalogSearchBarTrans.catalogRoutePlaceholder()}
            isFocused={searchFocused}
          />
          {isMobile && searchFocused ? null : eventId && eventHandle ? (
            <RegistryGiftBasketWidget
              eventId={eventId}
              eventHandle={eventHandle}
            />
          ) : (
            <WidgetTrigger
              onTriggerClick={currentUser.profile ? onSwitchEventClick : topLevelLinks.giftBasketWidget.onClick}
              registryItemCount={0}
              trackedGiftsCount={0}
              completionPercent={completionPercent}
            />
          )}
        </Flex>
      </NavContainer>
      <DropdownLinksContainer
        ref={linksDropdownRef}
        paddingY={8}
        transition={animationTransitionExt({ property: 'top', delay: isOpen ? '100ms' : undefined }, { property: 'background-color' })}
        {...getLinksContainerProps()}
      >
        <Box
          width={['100%']}
          marginX="auto"
        >
          {headerLinksType && (
            <DesktopMenu
              type={headerLinksType}
              getLinkProps={getHeaderLinkProps}
            />
          )}
        </Box>
      </DropdownLinksContainer>
    </Root>
  );
};

DesktopView.displayName = 'MarcomAppNavBarDesktop';
